import type { UrlObject } from 'url';
import { ParsedUrlQuery } from 'node:querystring';
import { LinkProps } from 'next/link';
import { NextPageContext } from 'next';
import {
  READY_DESKTOP_ROUTES_FOR_PRODUCTION,
  READY_ROUTES_FOR_PRODUCTION,
  ROUTES,
} from '@/constants/routes';
import { isDeepLink, makeUrlWithLang, startsWithHttp } from '@/utils/helpers';
import { mapQueryStringToPaymentData } from '@/views/Wallet/Finish.helpers';
import { CONFIG } from '@/constants/config';
import { QUERY } from '@/constants/common';

export function buildURL(data: UrlObject) {
  const baseUrl = window.location.origin;
  const url = new URL(baseUrl + (data.pathname || ''));

  if (data.query) {
    for (const [key, value] of Object.entries(data.query)) {
      if (value != null) {
        url.searchParams.append(key, value.toString());
      }
    }
  }

  if (data.hash) {
    url.hash = data.hash;
  }

  return url.href;
}

type AdRouteKeys =
  | 'main'
  | 'category'
  | 'subcategory'
  | 'vip'
  | 'new'
  | 'adDetails'
  | 'userAds'
  | 'shopDetails'
  | 'edit';

type CollectionRouteKeys = 'collections';

interface CheckRouteOptions {
  isDesktop?: boolean;
  context?: NextPageContext;
  query?: ParsedUrlQuery;
  lang?: string;
}

function isAdRoute(route: string, hrefString: string) {
  return route === ROUTES.ad && hrefString.includes(route);
}

function isWalletFinishRoute(route: string, hrefString: string) {
  return route === ROUTES.walletFinish && hrefString.includes(route);
}

function isValidWalletFinishRoute(context?: NextPageContext) {
  return Boolean(context?.query && mapQueryStringToPaymentData(context.query));
}

export function matchCollectionRoute(
  url: string,
  keysToCheck: CollectionRouteKeys[]
) {
  const routes: Record<CollectionRouteKeys, RegExp> = {
    collections: /^\/collections(\/[A-Za-z0-9_-]+)?$/,
  };

  return Object.keys(routes).some(key => {
    const routeKey = key as CollectionRouteKeys;
    if (routes[routeKey].test(url)) {
      return keysToCheck.includes(routeKey);
    }
    return false;
  });
}

export function matchAdRoute(url: string, keysToCheck: AdRouteKeys[]) {
  const routes: Record<AdRouteKeys, RegExp> = {
    main: /^\/elanlar(\?.*)?$/,
    new: /^\/elanlar(?:\/[^/]+(?:\/[^/]+)?)?\/new$/,
    vip: /^\/elanlar(?:\/[^/]+(?:\/[^/]+)?)?\/vip$/,
    category: /^\/elanlar\/(?!new$|vip$|lightweight_form$)[^/]+$/,
    subcategory:
      /^\/elanlar\/(?!new$|vip$|lightweight_form$)[^/]+\/(?!new$|vip$|lightweight_form$)[^/]+$/,
    adDetails:
      /^\/elanlar\/(?!new$|vip$|lightweight_form$)[^/]+\/[^/]+\/\d+(?:[?#].*)?$/,
    userAds: new RegExp(`^\\/elanlar\\?${QUERY.userId}=\\d+$`),
    shopDetails: /^\/shops\/[^/]+$/,
    edit: /^\/elanlar\/(?!new$|vip$|lightweight_form$)[^/]+\/[^/]+\/\d+\/ad_edits$/,
  };

  return Object.keys(routes).some(key => {
    const routeKey = key as AdRouteKeys;
    if (routes[routeKey].test(url)) {
      return keysToCheck.includes(routeKey);
    }
    return false;
  });
}

function handleAdRoute(hrefString: string, options?: CheckRouteOptions) {
  const { isDesktop, context } = options ?? {};

  if (context?.query?.cursor) return false; // for infinite scroll on the rails shop page

  const isDesktopReady = matchAdRoute(hrefString, [
    'main',
    'category',
    'subcategory',
    'adDetails',
    'userAds',
    'new',
    'edit',
    'vip',
  ]);
  const isMobileReady = matchAdRoute(hrefString, [
    'main',
    'category',
    'subcategory',
    'adDetails',
    'userAds',
    'new',
    'edit',
    'vip',
  ]);

  if (context && isMobileReady) {
    context.res?.setHeader(
      isDesktopReady ? 'x-adaptive-ready' : 'x-mobile-only',
      'true'
    );
  }

  return isDesktop ? isDesktopReady : isMobileReady;
}

function handleRegularRoute(
  route: string,
  hrefString: string,
  options?: CheckRouteOptions
) {
  const { isDesktop, context } = options ?? {};

  if (route === ROUTES.collections && hrefString.includes(route)) {
    const isReady = matchCollectionRoute(hrefString, ['collections']);

    if (context && isReady) {
      context.res?.setHeader('x-adaptive-ready', 'true');
    }

    return isReady;
  }

  if (route === ROUTES.shops && hrefString.includes(route)) {
    if (hrefString === ROUTES.shops) return true;
    const isReady = matchAdRoute(hrefString, ['shopDetails']);

    if (context && isReady) {
      context.res?.setHeader('x-adaptive-ready', 'true');
    }

    return isReady;
  }

  function checkIsMobileReady() {
    if (isWalletFinishRoute(route, hrefString)) {
      return isValidWalletFinishRoute(context);
    }

    return route === hrefString;
  }

  const isDesktopReady =
    route === hrefString && READY_DESKTOP_ROUTES_FOR_PRODUCTION.includes(route);
  const isMobileReady = checkIsMobileReady();

  if (context && isMobileReady) {
    context.res?.setHeader(
      isDesktopReady ? 'x-adaptive-ready' : 'x-mobile-only',
      'true'
    );
  }

  return isDesktop ? isDesktopReady : isMobileReady;
}

export function checkRoute(
  href: LinkProps['href'],
  options?: CheckRouteOptions
): {
  isInternal?: boolean;
  hrefString: string;
} {
  const isHrefString = typeof href === 'string';
  const hrefString = isHrefString ? href : href.pathname || buildURL(href);

  const isInternal = !!READY_ROUTES_FOR_PRODUCTION.find(r => {
    if (isAdRoute(r, hrefString)) {
      return handleAdRoute(hrefString, options);
    }

    return handleRegularRoute(r, hrefString, options);
  });

  if (!isInternal && !startsWithHttp(hrefString) && !isDeepLink(hrefString)) {
    const url = makeUrlWithLang({
      protocol: CONFIG.protocol,
      host: CONFIG.host,
      path: hrefString,
      lang: options?.lang,
    });
    return { isInternal, hrefString: url };
  }

  return { isInternal, hrefString };
}
